<template>
  <div>

    <!-- #region::Voucher image form -->
    <validation-observer ref="voucherImageForm">
      <b-form @submit="$event.preventDefault()">

        <b-modal
          id="voucher-image-modal"
          ref="voucher-image-modal"
          :title="idImage ? 'Actualizar' : 'Comprobante de pago de la comisión'"
          centered
          @hidden="onCloseModal"
        >
          <b-card-text v-if="commission">
            <b-row>

              <b-col md="12">
                <div
                  slot="label"
                  class="d-flex justify-content-start"
                >
                  <small>Imagen del comprobante</small>
                  <h5 class="text-danger">
                    *
                  </h5>
                </div>
              </b-col>

              <!-- #region::Images inputs group -->
              <b-col md="3">
                <b-form-group>
                  <ImagesLoader
                    ref="img01"
                    :image="images[0]"
                    :validate-size="true"
                    :accept-webp-format="false"
                    @image-loaded="imagesLoaded = true"
                  />
                </b-form-group>
              </b-col>

              <b-col md="12">
                <b-alert
                  variant="danger"
                  :show="!imagesLoaded && submitted"
                  class="mb-2"
                >
                  <div class="alert-body">
                    <small>Es necesario agregar imagen del recibo</small>
                  </div>
                </b-alert>
              </b-col>

              <b-col md="6">
                <small class="text-primary">Arrastra o selecciona la imagen</small>
              </b-col>
              <!-- #endregion::Images inputs group -->

            </b-row>
          </b-card-text>

          <!-- #region::Footer -->
          <template #modal-footer>
            <b-button
              variant="delete-btn"
              class="delete-btn"
              @click="hideModal"
            >
              Cancelar
            </b-button>
            <b-button
              variant="principal-btn"
              class="principal-btn"
              @click="onSaveVoucher"
            >
              {{ idImage ? 'Actualizar' : 'Cargar' }}
            </b-button>
          </template>
          <!-- #endregion::Footer -->

        </b-modal>

      </b-form>
    </validation-observer>
    <!-- #endregion::Voucher image form -->

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ValidationObserver, localize } from 'vee-validate'
import {
  BForm, BModal, VBModal, BCardText, BRow, BCol, BAlert, BFormGroup, BButton,
} from 'bootstrap-vue'

import getError from '@/helpers/ErrorsHandler'
import ImagesLoader from '@/components/forms/ImagesLoader.vue'
import convertImageToBase64 from '@/helpers/ConvertImageToBase64'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BModal,
    BAlert,
    BButton,
    BCardText,
    BFormGroup,
    ImagesLoader,
    ValidationObserver,
  },
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      images: [],
      commission: null,

      submitted: false,
      imagesLoaded: false,

      /**
       * Id de la imagen que se edita
       */
      idImage: null,
    }
  },
  computed: {
    ...mapGetters({
      getCommission: 'commissions/getCommission',
    }),
    /**
     * Información de la comisión
     */
    commissionId() {
      return this.getCommission.commission_id
    },
    commissionImageName() {
      return this.getCommission?.image_name
    },
    commissionImageUrl() {
      return this.getCommission?.url
    },
  },
  created() {
    localize('es')
  },
  methods: {
    ...mapActions({
      saveVoucherImage: 'commissions/saveVoucherImage',
      downloadVoucherImageBlob: 'commissions/downloadVoucherImageBlob',
    }),

    /**
     * Muestra el modal
     * @summary Muestra el modal y carga la imagen del recibo
     * @exception {Error} error - Error de validación si no se puede cargar la imagen
     */
    async showModalAndLoadVoucher() {
      try {
        const blob = await this.downloadVoucherImageBlob({
          commissionId: this.commissionId,
          fileName: this.commissionImageName,
        })
        const base64 = await convertImageToBase64(blob)

        this.images = [{ imagesCode: base64, image: this.commissionImageUrl }]

        this.commission = this.getCommission
        this.$refs['voucher-image-modal'].show()
      } catch (error) {
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },

    /**
     * Guarda la imagen del recibo
     * @summary Valida y guarda la imagen del recibo
     * @exception {Error} error - Error de validación si no se puede guardar la imagen
     */
    async onSaveVoucher() {
      const success = await this.$refs.voucherImageForm.validate()
      this.submitted = true

      if (!this.$refs.img01.file) {
        this.imagesLoaded = false
        return
      }

      if (success) {
        try {
          this.$swal({
            title: 'Espere por favor',
            allowOutsideClick: false,
          })

          this.$swal.showLoading()

          const formData = new FormData()

          if (this.commissionImageName) {
            formData.append('update', 1)
          }

          formData.append('ID', this.commissionId)
          formData.append('files[]', this.$refs.img01.file)

          // eslint-disable-next-line no-restricted-syntax
          for (const pair of formData.entries()) {
            console.log(`${pair[0]}, ${pair[1]}`)
          }

          await this.saveVoucherImage(formData)

          this.showToast(
            'Actualización de recibo',
            'La imagen del recibo se ha almacenado correctamente',
            'success',
          )

          this.hideModal()
          this.$emit('reload-commission')
        } catch (error) {
          this.showToast('Error de validación', getError(error), 'danger')
        } finally {
          this.$swal.close()
        }
      }
    },

    /**
     * Muestra el modal
     *
     * @summary Muestra el modal y establece la información del pago
     */
    showModal(commission) {
      this.commission = commission
      this.$refs['voucher-image-modal'].show()
    },

    /**
     * Cancelación de pago
     *
     * @summary Limpia los campos del formulario y oculta el modal
     */
    onCloseModal() {
      this.idImage = null
      this.submitted = false
    },

    /**
     * Cierre de modal
     *
     * @summary Oculta el modal
     */
    hideModal() {
      this.$refs['voucher-image-modal'].hide()
    },

    /**
    * Mensaje de notificación
    *
    * @summary Muestra un mensaje de notificación
    * @param {String} title - Título del mensaje
    * @param {String} text - Texto del mensaje
    * @param {String} variant - Tipo del mensaje
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
